import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, downloadPdf, uploadPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { CVPreview } from "@/components/CVPreview";
import { CompletionBadge } from "@/components/CompletionBadge";
import { PhotoCropDialog } from "@/components/PhotoCropDialog";
import { StatementStep } from "@/components/StatementStep";
import { ReferenceBrief } from "@/components/ReferenceBrief";
import { DeadlinesStep } from "@/components/DeadlinesStep";
import { T, LEVEL_OPTIONS, KVKK, bi, setBi, periodText } from "@/lib/i18n";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GraduationCap, Sun, FolderKanban, Trophy, BookOpen, Briefcase, Building2, Activity,
  BadgeCheck, Music, Piano, Languages, MonitorSmartphone, FileSpreadsheet, PenTool, Code2,
  ScrollText, Users, Award, FileText, HeartHandshake, Sparkles, Plus, Trash2, ChevronLeft,
  ChevronRight, ArrowLeft, Download, Wand2, Check, User, CircleDot, CheckCircle2,
  GripVertical, Camera, ShieldCheck, Share2, Copy, MessageSquare, PenLine, CalendarDays, AlertTriangle,
} from "lucide-react";

const ICONS = {
  GraduationCap, Sun, FolderKanban, Trophy, BookOpen, Briefcase, Building2, Activity,
  BadgeCheck, Music, Piano, Languages, MonitorSmartphone, FileSpreadsheet, PenTool, Code2,
  ScrollText, Users, Award, FileText, HeartHandshake, Sparkles,
};

const rid = () => Math.random().toString(16).slice(2, 10);

export default function Builder() {
  const { id } = useParams();
  const { user, lang } = useAuth();
  const t = T[lang];
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [cv, setCv] = useState(null);
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("saved");
  const [mobileView, setMobileView] = useState("form");
  const [uploading, setUploading] = useState(false);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [comments, setComments] = useState({});
  const [generating, setGenerating] = useState(false);
  const [approval, setApproval] = useState({ approval: null, valid: false });
  const [ai, setAi] = useState({ open: false, loading: false, questions: [], suggestion: "", key: null, rec: null });
  const firstLoad = useRef(true);
  const saveTimer = useRef(null);
  const pendingRef = useRef(null);
  const dragIndex = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => { if (user === false) navigate("/auth"); }, [user]);

  useEffect(() => {
    Promise.all([api.get("/config"), api.get(`/cvs/${id}`)])
      .then(([cfgRes, cvRes]) => {
        setConfig(cfgRes.data);
        setCv({
          title: cvRes.data.title || "CV",
          language: cvRes.data.language || lang,
          personal: cvRes.data.personal || {},
          categories: cvRes.data.categories || {},
          notes: cvRes.data.notes || {},
          consents: cvRes.data.consents || {},
          statement: cvRes.data.statement || {},
          deadlines: cvRes.data.deadlines || {},
        });
        setApproval({ approval: cvRes.data.approval, valid: cvRes.data.approvalValid });
        setComments(cvRes.data.comments || {});
        setShareToken(cvRes.data.shareToken || null);
      })
      .catch(() => { toast.error("CV yüklenemedi"); navigate("/dashboard"); });
  }, [id]);

  const [translating, setTranslating] = useState(false);

  const flush = useCallback(async () => {
    const data = pendingRef.current;
    if (!data) return;
    pendingRef.current = null;
    clearTimeout(saveTimer.current);
    try { await api.put(`/cvs/${id}`, data); } catch { /* keep going */ }
    if (!pendingRef.current) setStatus("saved");
  }, [id]);

  const persist = useCallback((next) => {
    setStatus("saving");
    pendingRef.current = { ...next, language: lang };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 1000);
  }, [lang, flush]);

  // Flush unsaved changes when the tab closes / user navigates away
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const data = pendingRef.current;
      if (!data) return;
      pendingRef.current = null;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cvs/${id}`, {
        method: "PUT", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("dsa_token")}` },
        body: JSON.stringify(data),
      }).catch(() => {});
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      onBeforeUnload({ preventDefault() {} });
    };
  }, [id]);

  const leaveToDashboard = async () => { await flush(); navigate("/dashboard"); };

  useEffect(() => {
    if (!cv) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    persist(cv);
  }, [cv]);

  useEffect(() => { if (cv && cv.language !== lang) setCv((c) => ({ ...c, language: lang })); }, [lang]);

  useEffect(() => {
    const iv = setInterval(() => {
      api.get(`/cvs/${id}`).then((r) => { setComments(r.data.comments || {}); setApproval({ approval: r.data.approval, valid: r.data.approvalValid }); }).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [id]);

  const steps = useMemo(() => {
    if (!config) return [];
    return [{ type: "personal" }, ...config.categories.map((c) => ({ type: "cat", cat: c })), { type: "deadlines" }, { type: "statement" }, { type: "consents" }];
  }, [config]);

  const catCount = (key) => (cv?.categories?.[key] || []).length;
  const completedCats = useMemo(() => {
    if (!config || !cv) return 0;
    return config.categories.filter((c) => catCount(c.key) > 0).length;
  }, [cv, config]);

  if (!cv || !config) {
    return (<div className="min-h-screen"><Navbar /><div className="max-w-5xl mx-auto px-6 py-20 text-[#64748b]">...</div></div>);
  }

  // Personal
  const getP = (f) => bi(cv.personal[f], lang);
  const setPBi = (f, v) => setCv((c) => ({ ...c, personal: { ...c.personal, [f]: setBi(c.personal[f], lang, v) } }));
  const setPPlain = (f, v) => setCv((c) => ({ ...c, personal: { ...c.personal, [f]: v } }));

  // Records
  const setRecords = (key, records) => setCv((c) => ({ ...c, categories: { ...c.categories, [key]: records } }));
  const addRecord = (key) => setRecords(key, [...(cv.categories[key] || []), { id: rid(), title: "", org: "", period: "", periodStart: "", periodEnd: "", gpa: "", level: "", detail: "" }]);
  const updateRecord = (key, recId, field, value) =>
    setRecords(key, (cv.categories[key] || []).map((r) => (r.id === recId ? { ...r, [field]: value } : r)));
  const updatePeriod = (key, recId, part, value) =>
    setRecords(key, (cv.categories[key] || []).map((r) => {
      if (r.id !== recId) return r;
      const next = { ...r, [part]: value };
      return { ...next, period: (next.periodStart || next.periodEnd) ? periodText(next.periodStart || "", next.periodEnd || "") : "" };
    }));
  const removeRecord = (key, recId) => setRecords(key, (cv.categories[key] || []).filter((r) => r.id !== recId));
  const moveRecord = (key, from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...(cv.categories[key] || [])];
    if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return;
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    setRecords(key, arr);
  };

  // Notes
  const getNote = (key) => bi(cv.notes[key], lang);
  const setNote = (key, v) => setCv((c) => ({ ...c, notes: { ...c.notes, [key]: setBi(c.notes[key], lang, v) } }));

  // Consents
  const setConsent = (field, val) =>
    setCv((c) => {
      const consents = { ...(c.consents || {}), [field]: val };
      if (consents.formSubmission || consents.dataSharing) {
        if (!consents.acceptedAt) consents.acceptedAt = new Date().toISOString();
      } else {
        consents.acceptedAt = null;
      }
      return { ...c, consents };
    });

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (fileInput.current) fileInput.current.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const onCropped = async (file) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const data = await uploadPhoto(id, file);
      setCv((c) => ({ ...c, personal: { ...c.personal, photoUrl: data.photoUrl, photoPath: data.photoPath } }));
      toast.success(t.saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload error");
    } finally {
      setUploading(false);
    }
  };

  const openShare = async () => {
    setShareDialog(true);
    if (shareToken) return;
    try { const { data } = await api.post(`/cvs/${id}/share`); setShareToken(data.shareToken); }
    catch { toast.error("Link error"); }
  };
  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t.copied); }
    catch { toast.error("Copy error"); }
  };

  const goToStep = (key) => {
    if (key === "personal") setStep(0);
    else if (key === "consents") setStep(steps.length - 1);
    else if (key === "statement") setStep(steps.length - 2);
    else if (key === "deadlines") setStep(steps.length - 3);
    else { const i = config.categories.findIndex((c) => c.key === key); if (i >= 0) setStep(i + 1); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openAi = async (cat, rec) => {
    setAi({ open: true, loading: true, questions: [], suggestion: "", key: cat.key, rec: rec.id });
    try {
      const { data } = await api.post("/ai/followup", { categoryKey: cat.key, language: lang, title: bi(rec.title, lang), detail: bi(rec.detail, lang), targetProgram: bi(cv.personal?.targetProgram, lang) });
      setAi((a) => ({ ...a, loading: false, questions: data.questions || [], suggestion: data.suggestion || "" }));
    } catch { setAi((a) => ({ ...a, loading: false, questions: [], suggestion: "" })); }
  };

  const useSuggestion = () => {
    if (ai.suggestion && ai.key && ai.rec) {
      const cur = (cv.categories[ai.key] || []).find((r) => r.id === ai.rec);
      const curDetail = bi(cur?.detail, lang);
      const merged = curDetail ? `${curDetail} ${ai.suggestion}` : ai.suggestion;
      updateRecord(ai.key, ai.rec, "detail", setBi(cur?.detail, lang, merged));
      toast.success(t.saved);
    }
    setAi((a) => ({ ...a, open: false }));
  };

  const setStatement = (statement) => setCv((c) => ({ ...c, statement }));
  const generateStatement = async () => {
    setGenerating(true);
    try {
      await flush();
      const { data } = await api.post(`/cvs/${id}/statement/generate`, { language: lang });
      setCv((c) => {
        const st = c.statement || {};
        const answers = { ...(st.answers || {}) };
        const prev = Object.fromEntries(["q1", "q2", "q3"].map((q) => [q, bi(answers[q], lang)]));
        let versions = st.versions || [];
        if (Object.values(prev).some(Boolean)) {
          const same = versions.filter((v) => v.language === lang);
          const drop = same.length >= 5 ? same[0].id : null;
          versions = [...versions.filter((v) => v.id !== drop), { id: `${Date.now()}`, language: lang, answers: prev, createdAt: new Date().toISOString() }];
        }
        for (const q of ["q1", "q2", "q3"]) answers[q] = setBi(answers[q], lang, data.answers[q] || "");
        return { ...c, statement: { ...st, answers, versions, generatedAt: data.generatedAt } };
      });
      return data;
    } catch { toast.error("AI error"); return null; }
    finally { setGenerating(false); }
  };

  const translateMissing = async (target) => {
    const { data } = await api.post(`/cvs/${id}/translate`, { target });
    if (data.translated > 0) {
      pendingRef.current = null;
      setCv((c) => ({ ...c, personal: data.cv.personal, categories: data.cv.categories, notes: data.cv.notes, statement: { ...c.statement, motivation: data.cv.statement?.motivation, answers: data.cv.statement?.answers || c.statement?.answers } }));
      toast.success(`${data.translated} ${t.translatedN}`);
    }
    return data.translated;
  };

  const doDownload = async (dlLang) => {
    setPdfDialog(false);
    await flush();
    const base = (cv.personal?.fullName || cv.title || "cv").replace(/\s+/g, "_");
    const langs = dlLang === "both" ? ["tr", "en"] : [dlLang];
    try {
      for (const l of langs) {
        const tid = toast.loading(t.translating);
        try { await translateMissing(l); } catch { toast.error("AI translate error"); }
        toast.dismiss(tid);
        await downloadPdf(id, l, `${base}_${l}.pdf`);
      }
    } catch { toast.error("PDF error"); }
  };

  const translateNow = async (target) => {
    setTranslating(true);
    try { await flush(); const n = await translateMissing(target); if (n === 0) toast.success(lang === "tr" ? "Eksik çeviri yok" : "No missing translations"); }
    catch (e) { toast.error(e.response?.data?.detail || "AI translate error"); }
    finally { setTranslating(false); }
  };

  const current = steps[step];

  const openPdfDialog = () => {
    if (!(cv.consents?.formSubmission && cv.consents?.dataSharing)) {
      toast.error(t.consentRequiredBoth);
      setStep(steps.length - 1);
      setMobileView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setPdfDialog(true);
  };

  const renderFieldInputs = (cat) => {
    const fields = config.typeFields[cat.type];
    const heads = config.columnLabels[cat.type][lang];
    const records = cv.categories[cat.key] || [];
    return (
      <div className="space-y-4">
        {records.length === 0 && (
          <div className="text-sm text-[#94a3b8] italic border border-dashed border-[#cbd5e1] rounded-lg p-6 text-center">
            {lang === "tr" ? "Henüz kayıt yok. Aşağıdan ekleyebilirsin." : "No records yet. Add one below."}
          </div>
        )}
        {records.length > 1 && (
          <div className="text-[11px] text-[#94a3b8] flex items-center gap-1.5"><GripVertical className="h-3.5 w-3.5" /> {t.dragHint}</div>
        )}
        {records.map((r, idx) => (
          <div
            key={r.id}
            data-testid={`record-${cat.key}-${idx}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { moveRecord(cat.key, dragIndex.current, idx); dragIndex.current = null; }}
            className="border border-[#e2e8f0] rounded-xl p-5 bg-[#f8fafc]"
          >
            <div className="flex items-center justify-between mb-3">
              <div
                draggable
                onDragStart={() => { dragIndex.current = idx; }}
                data-testid={`drag-${cat.key}-${idx}`}
                className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing text-[#64748b]"
              >
                <GripVertical className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wide">#{idx + 1}</span>
              </div>
              <Button data-testid={`remove-record-${cat.key}-${idx}`} variant="ghost" size="sm" onClick={() => removeRecord(cat.key, r.id)} className="text-[#c0392b] hover:bg-[#fadbd8] h-7">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> {t.remove}
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {fields.filter((f) => f !== "detail").map((f) => (
                <div key={f} className={(cat.type === "standard" || cat.type === "education") && f === "title" ? "sm:col-span-2" : ""}>
                  <Label className="text-[13px] text-[#1a2e4a]">{heads[f]}</Label>
                  {f === "period" ? (
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div>
                        <Input data-testid={`input-${cat.key}-${idx}-periodStart`} value={r.periodStart || ""} onChange={(e) => updatePeriod(cat.key, r.id, "periodStart", e.target.value)} placeholder={t.periodStartPh} className="bg-white" />
                        <div className="text-[10px] text-[#94a3b8] mt-0.5">{t.periodStart}</div>
                      </div>
                      <div>
                        <Input data-testid={`input-${cat.key}-${idx}-periodEnd`} value={r.periodEnd || ""} onChange={(e) => updatePeriod(cat.key, r.id, "periodEnd", e.target.value)} placeholder={t.periodEndPh} className="bg-white" />
                        <div className="text-[10px] text-[#94a3b8] mt-0.5">{t.periodEnd}</div>
                      </div>
                    </div>
                  ) : f === "gpa" ? (
                    <Input data-testid={`input-${cat.key}-${idx}-gpa`} value={r.gpa || ""} onChange={(e) => updateRecord(cat.key, r.id, "gpa", e.target.value)} placeholder={lang === "tr" ? "örn. 92/100 veya 3.8/4.0" : "e.g. 92/100 or 3.8/4.0"} className="mt-1 bg-white" />
                  ) : f === "level" ? (
                    <>
                      <Input data-testid={`input-${cat.key}-${idx}-${f}`} list={`levels-${lang}`} value={bi(r[f], lang)} onChange={(e) => updateRecord(cat.key, r.id, f, setBi(r[f], lang, e.target.value))} className="mt-1 bg-white" />
                      <datalist id={`levels-${lang}`}>{LEVEL_OPTIONS[lang].map((o) => <option key={o} value={o} />)}</datalist>
                    </>
                  ) : (
                    <Input data-testid={`input-${cat.key}-${idx}-${f}`} value={bi(r[f], lang)} onChange={(e) => updateRecord(cat.key, r.id, f, setBi(r[f], lang, e.target.value))} className="mt-1 bg-white" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <Label className="text-[13px] text-[#1a2e4a]">{heads.detail}</Label>
                <Button data-testid={`ai-deepen-${cat.key}-${idx}`} variant="ghost" size="sm" onClick={() => openAi(cat, r)} className="text-[#c0392b] hover:bg-[#fadbd8] h-7 text-xs">
                  <Wand2 className="h-3.5 w-3.5 mr-1" /> {t.aiDeepen}
                </Button>
              </div>
              <Textarea data-testid={`input-${cat.key}-${idx}-detail`} value={bi(r.detail, lang)} onChange={(e) => updateRecord(cat.key, r.id, "detail", setBi(r.detail, lang, e.target.value))} rows={3} className="mt-1 bg-white" />
            </div>
          </div>
        ))}
        <Button data-testid={`add-record-${cat.key}`} onClick={() => addRecord(cat.key)} variant="outline" className="w-full border-dashed border-[#1a2e4a] text-[#1a2e4a] hover:bg-[#1a2e4a] hover:text-white h-11">
          <Plus className="h-4 w-4 mr-2" /> {t.addRecord}
        </Button>

        {/* Advisor note */}
        <div className="mt-2 rounded-lg border border-[#c0392b]/30 bg-[#fadbd8]/25 p-4">
          <Label className="text-[13px] font-bold text-[#c0392b] flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> {t.advisorNote}
          </Label>
          <Textarea data-testid={`note-${cat.key}`} value={getNote(cat.key)} onChange={(e) => setNote(cat.key, e.target.value)} rows={2} placeholder={t.advisorNotePh} className="mt-1 bg-white" />
        </div>

        {/* Advisor comments (from share link) */}
        {(comments[cat.key] || []).length > 0 && (
          <div data-testid={`comments-${cat.key}`} className="rounded-lg border border-[#1a2e4a]/20 bg-[#eef2f8]/60 p-4">
            <div className="text-[13px] font-bold text-[#1a2e4a] flex items-center gap-1.5 mb-2"><MessageSquare className="h-3.5 w-3.5" /> {t.advisorComments}</div>
            <ul className="space-y-2">
              {comments[cat.key].map((c) => (
                <li key={c.id} className="text-[13px] bg-white border border-[#e2e8f0] rounded-lg px-3 py-2">
                  <div className="flex justify-between text-[11px] text-[#64748b] mb-0.5"><span className="font-semibold text-[#1a2e4a]">{c.author}</span><span>{String(c.created_at).slice(0, 10)}</span></div>
                  <div className="text-[#334155] whitespace-pre-wrap">{c.text}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const langChip = (
    <span className="text-[11px] rounded-full bg-[#eef2f8] text-[#1a2e4a] px-2.5 py-1 font-semibold">
      {t.editingLang}: {lang.toUpperCase()}
    </span>
  );

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Builder top bar */}
      <div className="bg-white border-b border-[#e2e8f0] sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <Button data-testid="back-dashboard-btn" variant="ghost" onClick={leaveToDashboard} className="text-[#1a2e4a] h-9 px-2">
            <ArrowLeft className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t.backToDashboard}</span>
          </Button>
          <Input data-testid="cv-title-input" value={cv.title} onChange={(e) => setCv((c) => ({ ...c, title: e.target.value }))} className="h-9 w-[120px] sm:max-w-[200px] sm:w-auto font-semibold text-[#1a2e4a]" />
          <span data-testid="save-status" className={`text-xs flex items-center gap-1.5 ${status === "saving" ? "text-[#64748b]" : "text-green-600"}`}>
            {status === "saving" ? <><CircleDot className="h-3.5 w-3.5 animate-pulse" /> <span className="hidden sm:inline">{t.saving}</span></> : <><Check className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t.saved}</span></>}
          </span>
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2 flex-wrap">
            <CompletionBadge cv={cv} lang={lang} onGoTo={goToStep} />
            <div className="sm:hidden flex rounded-lg border border-[#e2e8f0] overflow-hidden text-xs">
              <button data-testid="mobile-view-form" onClick={() => setMobileView("form")} className={`px-3 py-1.5 ${mobileView === "form" ? "bg-[#1a2e4a] text-white" : "text-[#1a2e4a]"}`}>{t.editForm}</button>
              <button data-testid="mobile-view-preview" onClick={() => setMobileView("preview")} className={`px-3 py-1.5 ${mobileView === "preview" ? "bg-[#1a2e4a] text-white" : "text-[#1a2e4a]"}`}>{t.livePreview}</button>
            </div>
            <Button data-testid="share-link-btn" onClick={openShare} variant="outline" className="border-[#1a2e4a] text-[#1a2e4a] h-9">
              <Share2 className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{t.shareLink}</span>
            </Button>
            <Button data-testid="download-pdf-btn" onClick={openPdfDialog} className="bg-[#c0392b] hover:bg-[#a93226] text-white h-9">
              <Download className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{t.downloadPdf}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[240px_1fr_1fr] gap-6">
        {/* Category nav */}
        <aside data-testid="wizard-category-nav" className="hidden lg:block">
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-3 sticky top-32">
            <div className="px-2 pb-3">
              <div className="flex items-center justify-between text-xs text-[#64748b] mb-1.5">
                <span>{completedCats}/{config.categories.length} {t.completed}</span>
              </div>
              <Progress value={(completedCats / config.categories.length) * 100} className="h-1.5" />
            </div>
            <div className="max-h-[58vh] overflow-y-auto thin-scroll">
              <button onClick={() => setStep(0)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${step === 0 ? "bg-[#1a2e4a] text-white" : "text-[#334155] hover:bg-[#f1f5f9]"}`}>
                <User className="h-4 w-4 shrink-0" /> {t.personalInfo}
              </button>
              {config.categories.map((c, i) => {
                const Icon = ICONS[c.icon] || CircleDot;
                const active = step === i + 1;
                const cnt = catCount(c.key);
                return (
                  <button key={c.key} data-testid={`nav-cat-${c.key}`} onClick={() => setStep(i + 1)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${active ? "bg-[#1a2e4a] text-white" : "text-[#334155] hover:bg-[#f1f5f9]"}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{c.label[lang]}</span>
                    {(comments[c.key] || []).length > 0 && <MessageSquare data-testid={`nav-comment-dot-${c.key}`} className={`h-3 w-3 shrink-0 ${active ? "text-[#f3c9c4]" : "text-[#c0392b]"}`} />}
                    {cnt > 0 && <span className={`text-[10px] rounded-full px-1.5 ${active ? "bg-white text-[#1a2e4a]" : "bg-[#1a2e4a] text-white"}`}>{cnt}</span>}
                  </button>
                );
              })}
              <button data-testid="nav-deadlines" onClick={() => setStep(steps.length - 3)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${step === steps.length - 3 ? "bg-[#1a2e4a] text-white" : "text-[#334155] hover:bg-[#f1f5f9]"}`}>
                <CalendarDays className="h-4 w-4 shrink-0" /> {t.deadlinesStep}
              </button>
              <button data-testid="nav-statement" onClick={() => setStep(steps.length - 2)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${step === steps.length - 2 ? "bg-[#1a2e4a] text-white" : "text-[#334155] hover:bg-[#f1f5f9]"}`}>
                <PenLine className="h-4 w-4 shrink-0" /> {t.statementStep}
              </button>
              <button data-testid="nav-consents" onClick={() => setStep(steps.length - 1)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${step === steps.length - 1 ? "bg-[#1a2e4a] text-white" : "text-[#334155] hover:bg-[#f1f5f9]"}`}>
                <ShieldCheck className="h-4 w-4 shrink-0" /> {t.consentsTitle}
              </button>
            </div>
          </div>
        </aside>

        {/* Form / wizard */}
        <section className={`min-w-0 ${mobileView === "preview" ? "hidden lg:block" : ""}`}>
          <select data-testid="mobile-step-select" value={step} onChange={(e) => { setStep(Number(e.target.value)); window.scrollTo({ top: 0 }); }} className="lg:hidden w-full mb-3 h-10 rounded-lg border border-[#e2e8f0] bg-white px-3 text-sm font-semibold text-[#1a2e4a]">
            {steps.map((s, i) => (
              <option key={i} value={i}>
                {i + 1}. {s.type === "personal" ? t.personalInfo : s.type === "cat" ? s.cat.label[lang] : s.type === "deadlines" ? t.deadlinesStep : s.type === "statement" ? t.statementStep : t.consentsTitle}
              </option>
            ))}
          </select>
          <div key={step} data-testid="question-card" className="bg-white rounded-xl border border-[#e2e8f0] p-6 fade-in">
            {current.type === "personal" ? (
              <>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-[#c0392b]" />
                    <h2 className="font-display font-bold text-2xl text-[#1a2e4a] sec-underline pb-2">{t.personalInfo}</h2>
                  </div>
                  {langChip}
                </div>
                <p className="text-sm text-[#64748b] mt-3 mb-5">{t.langSwitchHint}</p>

                {/* Photo */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-[84px] h-[104px] rounded-lg border-2 border-[#e2e8f0] bg-[#f1f5f9] overflow-hidden flex items-center justify-center">
                    {cv.personal.photoUrl ? (
                      <AuthImage src={cv.personal.photoUrl} alt="photo" className="w-full h-full object-cover" data-testid="photo-preview" />
                    ) : (
                      <Camera className="h-7 w-7 text-[#94a3b8]" />
                    )}
                  </div>
                  <div>
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickPhoto} className="hidden" data-testid="photo-file-input" />
                    <Button data-testid="upload-photo-btn" disabled={uploading} onClick={() => fileInput.current?.click()} variant="outline" className="border-[#1a2e4a] text-[#1a2e4a]">
                      <Camera className="h-4 w-4 mr-2" /> {uploading ? t.uploading : (cv.personal.photoUrl ? t.changePhoto : t.uploadPhoto)}
                    </Button>
                    <div className="text-[11px] text-[#94a3b8] mt-1.5">{t.photoHint}</div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.fullName}</Label><Input data-testid="personal-fullName" value={cv.personal.fullName || ""} onChange={(e) => setPPlain("fullName", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.targetProgram}</Label><Input data-testid="personal-targetProgram" value={getP("targetProgram")} onChange={(e) => setPBi("targetProgram", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.email}</Label><Input data-testid="personal-email" value={cv.personal.email || ""} onChange={(e) => setPPlain("email", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.phone}</Label><Input data-testid="personal-phone" value={cv.personal.phone || ""} onChange={(e) => setPPlain("phone", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.location}</Label><Input data-testid="personal-location" value={getP("location")} onChange={(e) => setPBi("location", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.birthDate}</Label><Input data-testid="personal-birthDate" value={cv.personal.birthDate || ""} onChange={(e) => setPPlain("birthDate", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-[13px] text-[#1a2e4a]">{t.nationality}</Label><Input data-testid="personal-nationality" value={getP("nationality")} onChange={(e) => setPBi("nationality", e.target.value)} className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label className="text-[13px] text-[#1a2e4a]">{t.summary}</Label><Textarea data-testid="personal-summary" value={getP("summary")} onChange={(e) => setPBi("summary", e.target.value)} rows={3} className="mt-1" /></div>
                </div>
              </>
            ) : current.type === "deadlines" ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <CalendarDays className="h-5 w-5 text-[#c0392b]" />
                  <h2 className="font-display font-bold text-2xl text-[#1a2e4a] sec-underline pb-2">{t.deadlinesStep}</h2>
                </div>
                <div className="mt-4">
                  <DeadlinesStep cvId={id} cv={cv} lang={lang} onChange={(deadlines) => setCv((c) => ({ ...c, deadlines }))} />
                </div>
              </>
            ) : current.type === "statement" ? (
              <>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <PenLine className="h-5 w-5 text-[#c0392b]" />
                    <h2 className="font-display font-bold text-2xl text-[#1a2e4a] sec-underline pb-2">{t.statementStep}</h2>
                  </div>
                  {langChip}
                </div>
                {(() => {
                  const ap = approval.approval;
                  const valid = ap && approval.valid && ap.forGeneratedAt === (cv.statement?.generatedAt || null);
                  return (
                    <div data-testid="approval-status" className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px] ${valid ? "border-green-200 bg-green-50 text-green-800" : ap ? "border-amber-200 bg-amber-50 text-amber-800" : "border-[#e2e8f0] bg-[#f8fafc] text-[#64748b]"}`}>
                      {valid ? <BadgeCheck className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                      <span>{valid ? `${t.approvalOk}: ${ap.author} · ${String(ap.approvedAt).slice(0, 10)}${ap.note ? ` — “${ap.note}”` : ""}` : ap ? t.approvalStale : t.approvalNone}</span>
                    </div>
                  );
                })()}
                <div className="mt-4">
                  <StatementStep cvId={id} cv={cv} lang={lang} onChange={setStatement} onGenerate={generateStatement} generating={generating} />
                </div>
                <div className="mt-6">
                  <ReferenceBrief cvId={id} cv={cv} lang={lang} onChange={setStatement} />
                </div>
              </>
            ) : current.type === "consents" ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-5 w-5 text-[#c0392b]" />
                  <h2 className="font-display font-bold text-2xl text-[#1a2e4a] sec-underline pb-2">{KVKK[lang].title}</h2>
                </div>
                <p className="text-sm text-[#64748b] mt-3 mb-4">{t.consentStepDesc}</p>
                <div className="text-sm text-[#334155] leading-relaxed bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4 max-h-52 overflow-y-auto thin-scroll">
                  {KVKK[lang].body}
                </div>
                <div className="mt-5 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="consent-form" checked={!!cv.consents.formSubmission} onCheckedChange={(v) => setConsent("formSubmission", !!v)} className="mt-0.5 data-[state=checked]:bg-[#c0392b] data-[state=checked]:border-[#c0392b]" />
                    <span className="text-sm text-[#334155]">{KVKK[lang].form} <span className="text-[#c0392b] font-bold">*</span></span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="consent-share" checked={!!cv.consents.dataSharing} onCheckedChange={(v) => setConsent("dataSharing", !!v)} className="mt-0.5 data-[state=checked]:bg-[#c0392b] data-[state=checked]:border-[#c0392b]" />
                    <span className="text-sm text-[#334155]">{KVKK[lang].share} <span className="text-[#c0392b] font-bold">*</span></span>
                  </label>
                </div>
                {!(cv.consents.formSubmission && cv.consents.dataSharing) && (
                  <div data-testid="consent-required-note" className="mt-3 text-xs text-[#c0392b] flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {t.consentRequiredBoth}</div>
                )}
                {cv.consents.acceptedAt && (
                  <div className="mt-4 text-xs text-green-600 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {String(cv.consents.acceptedAt).slice(0, 10)}</div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    {(() => { const Icon = ICONS[current.cat.icon] || CircleDot; return <Icon className="h-5 w-5 text-[#c0392b]" />; })()}
                    <h2 className="font-display font-bold text-2xl text-[#1a2e4a] sec-underline pb-2">{current.cat.label[lang]}</h2>
                  </div>
                  {langChip}
                </div>
                <p className="text-sm text-[#64748b] mt-3 mb-5 leading-relaxed">{current.cat.probe[lang]}</p>
                {renderFieldInputs(current.cat)}
              </>
            )}

            {/* Step nav */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#e2e8f0]">
              <Button data-testid="prev-step-btn" variant="outline" disabled={step === 0} onClick={() => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="border-[#1a2e4a] text-[#1a2e4a]">
                <ChevronLeft className="h-4 w-4 mr-1" /> {t.prev}
              </Button>
              <span className="text-xs text-[#64748b]">{t.step} {step + 1} {t.of} {steps.length}</span>
              {step < steps.length - 1 ? (
                <Button data-testid="next-step-btn" onClick={() => { setStep((s) => Math.min(steps.length - 1, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-[#1a2e4a] hover:bg-[#254066] text-white">
                  {t.next} <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button data-testid="finish-btn" onClick={openPdfDialog} className="bg-[#c0392b] hover:bg-[#a93226] text-white">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> {t.finish}
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Live preview */}
        <section className={`min-w-0 ${mobileView === "form" ? "hidden lg:block" : ""}`}>
          <div className="lg:sticky lg:top-32 min-w-0">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-[#64748b] font-bold">
              <FileText className="h-4 w-4 text-[#c0392b]" /> {t.livePreview}
            </div>
            <div className="overflow-x-auto thin-scroll min-w-0">
              <CVPreview cv={cv} config={config} lang={lang} />
            </div>
          </div>
        </section>
      </div>

      {/* PDF language dialog */}
      <Dialog open={pdfDialog} onOpenChange={setPdfDialog}>
        <DialogContent data-testid="pdf-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1a2e4a] flex items-center gap-2"><Download className="h-5 w-5 text-[#c0392b]" /> {t.pdfLangTitle}</DialogTitle>
            <DialogDescription className="text-[#64748b]">{t.pdfLangDesc}</DialogDescription>
          </DialogHeader>
          <div data-testid="translate-note" className="text-xs text-[#1a2e4a] bg-[#eef2f8] rounded-md px-3 py-2 flex items-start gap-2"><Languages className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#c0392b]" /><span>{t.translateNote}</span></div>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="translate-now-tr" variant="outline" size="sm" disabled={translating} onClick={() => translateNow("tr")} className="border-[#1a2e4a] text-[#1a2e4a] text-xs">{t.translateNow} (TR)</Button>
            <Button data-testid="translate-now-en" variant="outline" size="sm" disabled={translating} onClick={() => translateNow("en")} className="border-[#1a2e4a] text-[#1a2e4a] text-xs">{t.translateNow} (EN)</Button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Button data-testid="pdf-download-tr" onClick={() => doDownload("tr")} className="bg-[#1a2e4a] hover:bg-[#254066] text-white h-11">🇹🇷 {t.downloadTr}</Button>
            <Button data-testid="pdf-download-en" onClick={() => doDownload("en")} className="bg-[#1a2e4a] hover:bg-[#254066] text-white h-11">🇬🇧 {t.downloadEn}</Button>
          </div>
          <Button data-testid="pdf-download-both" onClick={() => doDownload("both")} className="w-full bg-[#c0392b] hover:bg-[#a93226] text-white h-11 mt-1">
            <Download className="h-4 w-4 mr-2" /> {t.downloadBoth}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Advisor share dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent data-testid="share-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1a2e4a] flex items-center gap-2"><Share2 className="h-5 w-5 text-[#c0392b]" /> {t.shareTitle}</DialogTitle>
            <DialogDescription className="text-[#64748b]">{t.shareDesc}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Input data-testid="share-url-input" readOnly value={shareUrl || "..."} onFocus={(e) => e.target.select()} className="text-xs font-mono" />
            <Button data-testid="share-copy-btn" disabled={!shareUrl} onClick={copyShare} className="bg-[#1a2e4a] hover:bg-[#254066] text-white shrink-0"><Copy className="h-4 w-4 mr-1.5" /> {t.copy}</Button>
          </div>
          {shareUrl && (
            <a data-testid="share-open-link" href={shareUrl} target="_blank" rel="noreferrer" className="text-xs text-[#c0392b] underline mt-1">{lang === "tr" ? "Danışman görünümünü yeni sekmede aç" : "Open advisor view in a new tab"}</a>
          )}
        </DialogContent>
      </Dialog>

      {cropSrc && <PhotoCropDialog src={cropSrc} lang={lang} onCancel={() => setCropSrc(null)} onDone={onCropped} />}

      {/* AI dialog */}
      <Dialog open={ai.open} onOpenChange={(o) => setAi((a) => ({ ...a, open: o }))}>
        <DialogContent data-testid="ai-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1a2e4a] flex items-center gap-2"><Wand2 className="h-5 w-5 text-[#c0392b]" /> {t.aiDeepen}</DialogTitle>
            <DialogDescription className="text-[#64748b]">
              {lang === "tr" ? "Cevabını somutlaştırmak için takip soruları ve önerilen yazım." : "Follow-up questions and a suggested wording to make your answer concrete."}
            </DialogDescription>
          </DialogHeader>
          {ai.loading ? (
            <div className="py-8 text-center text-[#64748b] flex items-center justify-center gap-2"><CircleDot className="h-4 w-4 animate-pulse text-[#c0392b]" /> {t.aiThinking}</div>
          ) : (
            <div className="space-y-4">
              {ai.questions.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#c0392b] mb-2">{t.aiQuestions}</div>
                  <ul className="space-y-2">
                    {ai.questions.map((q, i) => (<li key={i} data-testid={`ai-question-${i}`} className="text-sm text-[#334155] bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2">{q}</li>))}
                  </ul>
                </div>
              )}
              {ai.suggestion && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#1a2e4a] mb-2">{t.aiSuggestion}</div>
                  <div data-testid="ai-suggestion" className="text-sm text-[#334155] bg-[#fadbd8]/40 border border-[#c0392b]/30 rounded-lg px-3 py-2 italic">{ai.suggestion}</div>
                  <Button data-testid="use-suggestion-btn" onClick={useSuggestion} className="mt-3 w-full bg-[#1a2e4a] hover:bg-[#254066] text-white"><Check className="h-4 w-4 mr-1.5" /> {t.useSuggestion}</Button>
                </div>
              )}
              {!ai.questions.length && !ai.suggestion && (<div className="text-sm text-[#64748b] py-4 text-center">—</div>)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
